// src/Admin.jsx — Closet Collective admin control panel (/admin)
// Lightweight, password-gated panel to manage members, listings, and payouts
// without touching code. Uses the same Supabase REST helpers as the app.
import { useState, useEffect } from "react";
import {
  fetchListings, updateListing, deleteListing,
  fetchAllMembers, addMember, updateMember, deleteMember,
  fetchPayoutSplits, updatePayoutSplit, deletePayoutSplit, markSplitPaid,
} from "./lib/supabase";

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || "closet-admin";

const STYLE = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital@0;1&family=Epilogue:wght@300;400;500;600&display=swap');
:root{--bg:#F7F3EE;--surface:#fff;--surface2:#F2EDE6;--border:#E4DDD4;--border2:#D4CBBF;--text:#1A1714;--muted:#7A6F64;--accent:#C8874A;--green:#3D7A52;--green-l:#DFF0E6;--red:#C0392B;}
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
body{background:var(--bg);font-family:'Epilogue',sans-serif;color:var(--text);}
.admin{min-height:100vh;}
.admin-header{background:var(--text);padding:1.5rem 1.5rem;}
.admin-eyebrow{font-size:10px;font-weight:600;letter-spacing:.18em;text-transform:uppercase;color:var(--accent);}
.admin-title{font-family:'Playfair Display',serif;font-size:1.9rem;color:#F7F3EE;margin-top:4px;}
.admin-main{max-width:880px;margin:0 auto;padding:1.5rem 1.25rem 4rem;}
.tab-row{display:flex;gap:6px;margin-bottom:1.5rem;}
.tab{flex:1;padding:10px;border-radius:12px;text-align:center;font-size:13px;font-weight:500;cursor:pointer;border:1px solid var(--border);background:var(--surface);color:var(--muted);font-family:'Epilogue',sans-serif;}
.tab.active{background:var(--text);color:var(--bg);border-color:var(--text);}
.section-label{font-size:10px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;color:var(--muted);margin:18px 0 10px;}
.card{background:var(--surface);border:1px solid var(--border);border-radius:14px;padding:14px 16px;margin-bottom:10px;}
.row{display:flex;gap:10px;flex-wrap:wrap;align-items:flex-end;}
.fld{display:flex;flex-direction:column;gap:4px;}
.fld label{font-size:10px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;color:var(--muted);}
input,select{background:var(--surface2);border:1px solid var(--border);border-radius:9px;padding:8px 11px;font-family:'Epilogue',sans-serif;font-size:13px;color:var(--text);outline:none;}
input:focus,select:focus{border-color:var(--accent);background:var(--surface);}
.btn{font-size:12px;font-weight:600;padding:8px 14px;border-radius:9px;cursor:pointer;font-family:'Epilogue',sans-serif;border:1px solid var(--border2);background:var(--surface2);color:var(--text);}
.btn:hover{background:var(--border);}
.btn.primary{background:var(--text);color:var(--bg);border-color:var(--text);}
.btn.green{border-color:var(--green);color:var(--green);background:transparent;}
.btn.red{border-color:var(--red);color:var(--red);background:transparent;}
.btn:disabled{opacity:.4;cursor:not-allowed;}
.pill{font-size:10px;font-weight:600;padding:3px 9px;border-radius:99px;}
.muted{color:var(--muted);font-size:12px;}
.title-line{font-size:15px;font-weight:600;}
.gate{max-width:380px;margin:18vh auto 0;padding:0 1.25rem;text-align:center;}
.gate h1{font-family:'Playfair Display',serif;font-size:1.8rem;margin-bottom:6px;}
.gate p{color:var(--muted);font-size:13px;margin-bottom:18px;}
.gate input{width:100%;text-align:center;margin-bottom:10px;}
.gate .btn{width:100%;}
.bar{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;gap:8px;}
.toast{position:fixed;top:14px;left:50%;transform:translateX(-50%);background:var(--surface);border:1px solid var(--green);color:var(--green);border-radius:10px;padding:9px 16px;font-size:13px;font-weight:500;box-shadow:0 4px 18px rgba(0,0,0,.12);z-index:50;}
a.home{color:var(--accent);font-size:12px;text-decoration:none;}
`;

const STATUSES = ["available", "sold", "cancelled"];

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [pw, setPw] = useState("");
  const [pwError, setPwError] = useState(false);
  const [tab, setTab] = useState("members");
  const [members, setMembers] = useState([]);
  const [listings, setListings] = useState([]);
  const [splits, setSplits] = useState([]);
  const [toast, setToast] = useState(null);
  const flash = (m) => { setToast(m); setTimeout(() => setToast(null), 2500); };

  const loadAll = async () => {
    const [m, l, s] = await Promise.all([fetchAllMembers(), fetchListings(), fetchPayoutSplits()]);
    setMembers(m); setListings(l); setSplits(s);
  };
  useEffect(() => { if (authed) loadAll(); }, [authed]);

  const tryLogin = () => {
    if (pw === ADMIN_PASSWORD) { setAuthed(true); setPwError(false); }
    else setPwError(true);
  };

  if (!authed) {
    return (
      <>
        <style>{STYLE}</style>
        <div className="gate">
          <h1>Closet Collective</h1>
          <p>Admin access</p>
          <input type="password" placeholder="Password" value={pw}
            onChange={e => setPw(e.target.value)} onKeyDown={e => e.key === "Enter" && tryLogin()} autoFocus />
          {pwError && <div style={{ color: "var(--red)", fontSize: 12, marginBottom: 10 }}>Incorrect password.</div>}
          <button className="btn primary" onClick={tryLogin}>Enter</button>
          <div style={{ marginTop: 16 }}><a className="home" href="/">← Back to site</a></div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{STYLE}</style>
      {toast && <div className="toast">{toast}</div>}
      <div className="admin">
        <div className="admin-header">
          <div className="admin-eyebrow">Admin Control Panel</div>
          <div className="admin-title">Closet Collective</div>
        </div>
        <div className="admin-main">
          <div className="bar">
            <a className="home" href="/">← Back to site</a>
            <button className="btn" onClick={loadAll}>↻ Refresh</button>
          </div>
          <div className="tab-row">
            <button className={`tab ${tab === "members" ? "active" : ""}`} onClick={() => setTab("members")}>Members ({members.length})</button>
            <button className={`tab ${tab === "listings" ? "active" : ""}`} onClick={() => setTab("listings")}>Listings ({listings.length})</button>
            <button className={`tab ${tab === "payouts" ? "active" : ""}`} onClick={() => setTab("payouts")}>Payouts ({splits.length})</button>
          </div>

          {tab === "members" && <Members members={members} reload={loadAll} flash={flash} />}
          {tab === "listings" && <Listings listings={listings} members={members} reload={loadAll} flash={flash} />}
          {tab === "payouts" && <Payouts splits={splits} listings={listings} reload={loadAll} flash={flash} />}
        </div>
      </div>
    </>
  );
}

// ── Members ─────────────────────────────────────────────────────────────────
function Members({ members, reload, flash }) {
  const [newName, setNewName] = useState("");
  const [newVenmo, setNewVenmo] = useState("");
  const [confirmId, setConfirmId] = useState(null);

  const add = async () => {
    if (!newName.trim()) return;
    await addMember({ name: newName.trim(), venmo_handle: newVenmo.trim() });
    setNewName(""); setNewVenmo(""); await reload(); flash("Member added");
  };

  return (
    <div>
      <div className="section-label">Add member</div>
      <div className="card">
        <div className="row">
          <div className="fld"><label>Name</label><input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Sam" /></div>
          <div className="fld"><label>Venmo</label><input value={newVenmo} onChange={e => setNewVenmo(e.target.value)} placeholder="@sam-resells" /></div>
          <button className="btn primary" onClick={add}>Add member</button>
        </div>
      </div>

      <div className="section-label">All members</div>
      {members.map(m => (
        <MemberRow key={m.id} m={m} reload={reload} flash={flash} confirmId={confirmId} setConfirmId={setConfirmId} />
      ))}
    </div>
  );
}

function MemberRow({ m, reload, flash, confirmId, setConfirmId }) {
  const [name, setName] = useState(m.name);
  const [venmo, setVenmo] = useState(m.venmo_handle || "");
  const [role, setRole] = useState(m.role);
  const dirty = name !== m.name || venmo !== (m.venmo_handle || "") || role !== m.role;

  const save = async () => {
    await updateMember(m.id, { name: name.trim(), venmo_handle: venmo.trim() || null, role });
    await reload(); flash("Member saved");
  };
  const remove = async () => { await deleteMember(m.id); setConfirmId(null); await reload(); flash("Member deleted"); };

  return (
    <div className="card">
      <div className="row">
        <div className="fld"><label>Name</label><input value={name} onChange={e => setName(e.target.value)} /></div>
        <div className="fld"><label>Venmo</label><input value={venmo} onChange={e => setVenmo(e.target.value)} placeholder="—" /></div>
        <div className="fld"><label>Role</label>
          <select value={role} onChange={e => setRole(e.target.value)}>
            <option value="seller">seller</option>
            <option value="admin">admin</option>
          </select>
        </div>
        <button className="btn primary" disabled={!dirty} onClick={save}>Save</button>
        {confirmId === m.id ? (
          <>
            <span className="muted" style={{ color: "var(--red)" }}>Delete?</span>
            <button className="btn red" onClick={remove}>Yes</button>
            <button className="btn" onClick={() => setConfirmId(null)}>No</button>
          </>
        ) : (
          <button className="btn red" onClick={() => setConfirmId(m.id)}>Delete</button>
        )}
      </div>
    </div>
  );
}

// ── Listings ────────────────────────────────────────────────────────────────
function Listings({ listings, members, reload, flash }) {
  return (
    <div>
      <div className="section-label">All listings ({listings.length})</div>
      {listings.length === 0 && <div className="muted">No listings yet.</div>}
      {listings.map(l => <ListingRow key={l.id} l={l} members={members} reload={reload} flash={flash} />)}
    </div>
  );
}

function ListingRow({ l, members, reload, flash }) {
  const [status, setStatus] = useState(l.status || "available");
  const [sellerId, setSellerId] = useState(l.seller_id || "");
  const [salePrice, setSalePrice] = useState(l.sale_price != null ? String(l.sale_price) : "");
  const [cost, setCost] = useState(l.item_cost != null ? String(l.item_cost) : "");
  const [fee, setFee] = useState(l.platform_fee != null ? String(l.platform_fee) : "");
  const [confirm, setConfirm] = useState(false);

  const save = async () => {
    const seller = members.find(x => x.id === sellerId);
    await updateListing(l.id, {
      status, sold: status === "sold",
      seller_id: sellerId || null,
      seller_name: seller ? seller.name : l.seller_name,
      sale_price: salePrice === "" ? null : Number(salePrice),
      item_cost: cost === "" ? 0 : Number(cost),
      platform_fee: fee === "" ? 0 : Number(fee),
    });
    await reload(); flash("Listing saved");
  };
  const remove = async () => { await deleteListing(l.id); setConfirm(false); await reload(); flash("Listing deleted"); };

  return (
    <div className="card">
      <div className="bar">
        <span className="title-line">{l.item_name || "(untitled)"}</span>
        <span className="muted">{[l.category, l.brand, l.platform].filter(Boolean).join(" · ")}</span>
      </div>
      <div className="row">
        <div className="fld"><label>Status</label>
          <select value={status} onChange={e => setStatus(e.target.value)}>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="fld"><label>Seller</label>
          <select value={sellerId} onChange={e => setSellerId(e.target.value)}>
            <option value="">— none —</option>
            {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
        <div className="fld" style={{ width: 90 }}><label>Sale $</label><input value={salePrice} onChange={e => setSalePrice(e.target.value)} placeholder="—" /></div>
        <div className="fld" style={{ width: 90 }}><label>Cost $</label><input value={cost} onChange={e => setCost(e.target.value)} /></div>
        <div className="fld" style={{ width: 90 }}><label>Fee $</label><input value={fee} onChange={e => setFee(e.target.value)} /></div>
        <button className="btn primary" onClick={save}>Save</button>
        {confirm ? (
          <>
            <span className="muted" style={{ color: "var(--red)" }}>Delete?</span>
            <button className="btn red" onClick={remove}>Yes</button>
            <button className="btn" onClick={() => setConfirm(false)}>No</button>
          </>
        ) : (
          <button className="btn red" onClick={() => setConfirm(true)}>Delete</button>
        )}
      </div>
    </div>
  );
}

// ── Payouts ─────────────────────────────────────────────────────────────────
function Payouts({ splits, listings, reload, flash }) {
  const listingName = (id) => listings.find(l => l.id === id)?.item_name || "(deleted item)";
  const unpaidTotal = splits.filter(s => !s.paid).reduce((t, s) => t + Number(s.amount || 0), 0);

  return (
    <div>
      <div className="section-label">Outstanding total: ${unpaidTotal.toFixed(2)}</div>
      {splits.length === 0 && <div className="muted">No payouts yet.</div>}
      {splits.map(s => <PayoutRow key={s.id} s={s} listingName={listingName} reload={reload} flash={flash} />)}
    </div>
  );
}

function PayoutRow({ s, listingName, reload, flash }) {
  const [amount, setAmount] = useState(String(s.amount ?? 0));
  const [confirm, setConfirm] = useState(false);
  const dirty = Number(amount) !== Number(s.amount);

  const save = async () => { await updatePayoutSplit(s.id, { amount: Number(amount) }); await reload(); flash("Payout updated"); };
  const togglePaid = async () => { await markSplitPaid(s.id, !s.paid); await reload(); flash(s.paid ? "Marked unpaid" : "Marked paid"); };
  const remove = async () => { await deletePayoutSplit(s.id); setConfirm(false); await reload(); flash("Payout deleted"); };

  return (
    <div className="card">
      <div className="bar">
        <span className="title-line">{s.member_name}</span>
        <span className="pill" style={{ background: s.paid ? "var(--green-l)" : "#FDEEE2", color: s.paid ? "var(--green)" : "var(--accent)" }}>
          {s.paid ? "paid" : "unpaid"}
        </span>
      </div>
      <div className="muted" style={{ marginBottom: 8 }}>{listingName(s.listing_id)}{s.split_pct != null ? ` · ${s.split_pct}%` : ""}</div>
      <div className="row">
        <div className="fld" style={{ width: 110 }}><label>Amount $</label><input value={amount} onChange={e => setAmount(e.target.value)} /></div>
        <button className="btn primary" disabled={!dirty} onClick={save}>Save</button>
        <button className={`btn ${s.paid ? "" : "green"}`} onClick={togglePaid}>{s.paid ? "Mark unpaid" : "Mark paid"}</button>
        {confirm ? (
          <>
            <span className="muted" style={{ color: "var(--red)" }}>Delete?</span>
            <button className="btn red" onClick={remove}>Yes</button>
            <button className="btn" onClick={() => setConfirm(false)}>No</button>
          </>
        ) : (
          <button className="btn red" onClick={() => setConfirm(true)}>Delete</button>
        )}
      </div>
    </div>
  );
}
